import { Icon } from '@natera/material/lib/icon'
import { useState } from 'react'
import { useAuditTrail } from '~/features/audit-trail'
import { DOCUMENT_FORM_HISTORY, useStepSession } from '~/features/document-analytics'
import { trackEvent } from '~/shared/lib/analytics'
import { cn } from '~/shared/lib/cn'
import { Button, IconButton, LocalIcon, Tooltip } from '~/shared/ui'
import { ErrorAlert } from './error-alert'
import { SubmitButton } from './submit-button'

interface ReviewActionsProps {
  className?: string
  isDirty: boolean
  isSubmitDisabled: boolean
  onSubmit: () => void
  submitLabel: string
  submitTooltip?: string
  canUndo?: boolean
  canRedo?: boolean
  revertButtonType?: 'revert-opti' | 'restore-edits'
  onRevert?: () => void
  revertForceDisabled?: boolean
  revertLabel?: string
  onUndo?: () => void
  onRedo?: () => void
  previousStepUrl?: string
  previousStepLabel?: string
  isError?: boolean
  errorContent?: string
  onRestorePreviousEdits?: () => void
  isScrolled?: boolean
  compactWhenAuditTrailVisible?: boolean
}

export function ReviewActions({
  className,
  isDirty,
  isSubmitDisabled,
  onSubmit,
  submitLabel,
  submitTooltip,
  canUndo,
  canRedo,
  revertButtonType = 'revert-opti',
  onRevert,
  revertForceDisabled,
  revertLabel,
  onUndo,
  onRedo,
  previousStepUrl,
  previousStepLabel,
  isError,
  errorContent,
  isScrolled,
  compactWhenAuditTrailVisible,
}: ReviewActionsProps) {
  const [isErrorHiddenManually, setIsErrorHiddenManually] = useState(false)
  const { isAuditTrailVisible } = useAuditTrail()
  const { step, documentKey } = useStepSession()

  const trackFormHistory = (interactionType: 'undo' | 'redo' | 'revert') => {
    trackEvent(DOCUMENT_FORM_HISTORY, {
      step,
      document_key: documentKey,
      interaction_type: interactionType,
    })
  }

  return (
    <div
      className={cn(
        'sticky bottom-0 z-5 w-full',
        {
          'shadow-popover-inverse transition-shadow': isScrolled,
        },
        className,
      )}
    >
      {isError && !isErrorHiddenManually && (
        <ErrorAlert
          content={errorContent || 'Something went wrong. Please try again.'}
          onClose={() => setIsErrorHiddenManually(true)}
        />
      )}
      <div className="flex w-full items-center justify-between bg-white px-6 py-4">
        {!!previousStepUrl && (
          <Button
            size="small"
            materialIcon="chevron_left"
            to={previousStepUrl}
          >
            {isAuditTrailVisible && compactWhenAuditTrailVisible ? undefined : previousStepLabel}
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          {onRevert ? (
            <Button
              size="small"
              unelevated
              tonal
              disabled={revertForceDisabled !== undefined ? revertForceDisabled : !isDirty}
              onClick={() => {
                trackFormHistory('revert')
                onRevert()
              }}
            >
              {revertButtonType === 'revert-opti' ? (
                <span className="flex items-center gap-1.5">
                  <LocalIcon name="opti" />
                  {revertLabel || 'Revert to original'}
                </span>
              ) : (
                <span className="flex items-center">
                  <Icon
                    className="text-[18px]"
                    children="cached"
                  />
                  Restore previous edits
                </span>
              )}
            </Button>
          ) : null}
          {onUndo ? (
            <Tooltip content="Undo">
              <IconButton
                size="small"
                container="small"
                materialIcon="undo"
                title="Undo"
                onClick={() => {
                  trackFormHistory('undo')
                  onUndo()
                }}
                disabled={!canUndo}
              />
            </Tooltip>
          ) : null}
          {onRedo ? (
            <Tooltip content="Redo">
              <IconButton
                size="small"
                container="small"
                materialIcon="redo"
                title="Redo"
                onClick={() => {
                  trackFormHistory('redo')
                  onRedo()
                }}
                disabled={!canRedo}
              />
            </Tooltip>
          ) : null}
          <SubmitButton
            isDisabled={isSubmitDisabled}
            onSubmit={onSubmit}
            label={submitLabel}
            tooltip={submitTooltip}
            onErrorReset={
              isErrorHiddenManually
                ? () => {
                    setTimeout(() => {
                      setIsErrorHiddenManually(false)
                    }, 0)
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  )
}
